/**
 * markdownTemplate.ts
 *
 * A lightweight markdown templating engine using ${variable name} syntax.
 * Variable names support spaces, multi-word names, and dot/bracket path notation
 * for accessing nested object properties and array indices.
 *
 * Pipe formatters
 * ───────────────
 * A pipe suffix can be appended to any path expression:
 *
 *   ${createdAt|date:ll}        — format with dayjs using the given dayjs format token
 *   ${createdAt|date:YYYY-MM}   — any dayjs format string works
 *
 * The dayjs locale used is the one currently active on the dayjs singleton.
 * Set it externally (e.g. via Lingui's locale change handler) before rendering.
 *
 * Usage:
 *   import { extractVariables, applyTemplate, renderTemplate } from './markdownTemplate';
 *
 *   const md = "# Hello, ${user.name}!\nYou are item ${list[0]}.";
 *   const vars: TemplateVariables = { user: { name: "Alex" }, list: ["first"] };
 *
 *   const rendered = applyTemplate(md, vars);
 *   // => "# Hello, Alex!\nYou are item first."
 *
 * Path syntax:
 *   ${name}           — top-level key (strings, numbers, booleans, objects)
 *   ${user.name}      — nested object property
 *   ${list[0]}        — array index
 *   ${user.tags[1]}   — mixed dot + bracket notation
 *   ${"first name"}   — quoted key for names with spaces (top-level only)
 *
 * Pipe syntax:
 *   ${createdAt|date:ll}   — resolve path, then apply the named formatter
 */

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(localizedFormat);

// Matches ${path} or ${path|formatter:arg}
// Group 1: path expression (trimmed)
// Group 2: formatter name (optional, e.g. "date")
// Group 3: formatter argument (optional, e.g. "ll")
const VAR_REGEX = /\$\{([^|}]+)(?:\|(\w+)(?::([^}]*))?)?\}/g;

/** Any JSON-compatible value. */
export type TemplateValue = string | number | boolean | null | TemplateObject | TemplateArray;
export interface TemplateObject {
  [key: string]: TemplateValue;
}
export interface TemplateArray extends Array<TemplateValue> {}

/** Map of top-level variable names → values (strings, objects, arrays, primitives). */
export type TemplateVariables = Record<string, TemplateValue>;

// ---------------------------------------------------------------------------
// Pipe formatters
// ---------------------------------------------------------------------------

type FormatterFn = (value: string, arg?: string, locale?: string) => string;

// Amendment workflow step id -> English status label, for status bars that need to reflect an
// amendment's real current state (e.g. the View form, which any non-edit-access user can open at
// any point in the workflow, so a static per-form label is wrong there).
const AMENDMENT_STATUS_LABELS: Record<string, string> = {
  amend_reports: 'Amendment draft',
  amend_reports_institution: 'Amendment draft',
  notification_1: 'Amendment draft',
  amend_data: 'Pending institution response',
  check_amendment_remarks: 'Pending institution response',
  amendment_request_received: 'Under OCIF review',
  internal_amendment_request: 'Under OCIF review',
  report_review_1: 'Under OCIF review',
  data_modifications: 'Under OCIF review',
  report_review_3: 'Under OCIF review',
  ApplyToReport: 'Under OCIF review',
  dumpanalytics: 'Under OCIF review',
  report_review_2: 'Returned to institution',
  Completed: 'Completed',
};

const FORMATTERS: Record<string, FormatterFn> = {
  /**
   * Format a date value using dayjs.
   * Respects whatever locale is currently active on the dayjs singleton.
   *
   * Usage: ${createdAt|date:ll}
   */
  date: (value, arg = 'LL', locale = 'en') => {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.locale(locale).format(arg) : value;
  },

  /**
   * Map an amendment's raw internal workflowStep id to an English status label.
   * Unrecognized step ids pass through unchanged rather than disappearing silently.
   *
   * Usage: ${workflowStep[0]|amendmentStatus}
   */
  amendmentStatus: value => AMENDMENT_STATUS_LABELS[value] ?? value,
};

// ---------------------------------------------------------------------------
// Path resolution
// ---------------------------------------------------------------------------

/**
 * Parse a path expression into segments.
 * Handles dot notation (`a.b.c`) and bracket notation (`a[0][1]`), or mixed.
 * Quoted top-level keys like `"first name"` or `'first name'` are returned as-is.
 *
 * @example
 * parsePath("user.tags[1]") // => ["user", "tags", "1"]
 * parsePath('"first name"') // => ["first name"]
 */
function parsePath(path: string): string[] {
  const trimmed = path.trim();

  // Quoted key — treat entire path as a single top-level key (supports spaces)
  if (/^["'].*["']$/.test(trimmed)) {
    return [trimmed.slice(1, -1)];
  }

  // Split on dots and brackets, filtering empty strings
  return trimmed
    .replace(/\[(\d+)\]/g, '.$1') // convert [0] → .0
    .split('.')
    .filter(Boolean);
}

/**
 * Resolve a dot/bracket path against a variables map.
 * Returns the resolved primitive value, or `undefined` if any segment is missing.
 *
 * @example
 * resolvePath({ user: { name: "Alex" } }, "user.name") // => "Alex"
 * resolvePath({ list: ["a", "b"] },        "list[1]")   // => "b"
 * resolvePath({ x: 1 },                   "missing")   // => undefined
 */
function resolvePath(variables: TemplateVariables, path: string): string | undefined {
  const segments = parsePath(path);

  let current: unknown = variables;

  for (const segment of segments) {
    if (current === null || current === undefined) return undefined;

    if (Array.isArray(current)) {
      const index = Number(segment);
      if (!Number.isInteger(index) || index < 0 || index >= (current as unknown[]).length) return undefined;
      current = (current as unknown[])[index];
    } else if (typeof current === 'object') {
      current = (current as Record<string, unknown>)[segment];
    } else {
      return undefined;
    }
  }

  if (current === null || current === undefined) return undefined;
  if (typeof current === 'object') return JSON.stringify(current);
  return String(current);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Options for applyTemplate / renderTemplate. */
export interface ApplyOptions {
  /**
   * Value to substitute when a variable has no match in the variables map.
   * Defaults to `null`, which preserves the original `${name}` token.
   */
  fallback?: string | null;
}

/** A function that converts a markdown string to HTML (e.g. `marked` or `markdown-it`). */
export type MarkdownRenderer = (markdown: string) => string;

/**
 * Extract all unique variable names (path only, without pipe formatters) from
 * a markdown template string.
 *
 * @param template - The markdown template
 * @returns Array of unique variable path names (trimmed)
 *
 * @example
 * extractVariables("Hello ${user.name}, started ${createdAt|date:ll}.")
 * // => ["user.name", "createdAt"]
 */
export function extractVariables(template: string): string[] {
  const vars = new Set<string>();
  for (const match of template.matchAll(VAR_REGEX)) {
    vars.add(match[1].trim());
  }
  return [...vars];
}

/**
 * Replace all `${path}` and `${path|formatter:arg}` placeholders in a markdown
 * template with the provided values. Variables with no matching value are left
 * as-is (or replaced with a fallback if provided).
 *
 * @param template  - The markdown template string
 * @param variables - Map of variable name → value
 * @param options   - Optional configuration
 * @returns Template with variables substituted
 *
 * @example
 * applyTemplate("Hello ${name}!", { name: "Alex" })
 * // => "Hello Alex!"
 *
 * applyTemplate("Started: ${createdAt|date:ll}", { createdAt: "2026-03-17T11:46:00Z" })
 * // => "Started: Mar 17, 2026"  (output depends on active dayjs locale)
 *
 * applyTemplate("Hello ${name}!", {}, { fallback: "(unknown)" })
 * // => "Hello (unknown)!"
 */
export function applyTemplate(template: string, variables: TemplateVariables, locale: string, options: ApplyOptions = {}): string {
  const { fallback = null } = options;

  return template.replace(VAR_REGEX, (original, path: string, formatterName?: string, formatterArg?: string) => {
    const resolved = resolvePath(variables, path);

    if (resolved === undefined) {
      return fallback !== null ? fallback : original;
    }

    if (formatterName) {
      const formatter = FORMATTERS[formatterName];
      if (formatter) {
        return formatter(resolved, formatterArg, locale);
      }
      // Unknown formatter — return raw resolved value
    }

    return resolved;
  });
}

/**
 * Extract variables from a template and return which ones are missing
 * from the provided variables map.
 *
 * @param template  - The markdown template string
 * @param variables - Map of variable name → value
 * @returns Variable names that are missing or empty
 *
 * @example
 * getMissingVariables("Hello ${user.name} from ${company}", { user: { name: "Alex" } })
 * // => ["company"]
 */
export function getMissingVariables(template: string, variables: TemplateVariables): string[] {
  return extractVariables(template).filter(key => {
    const resolved = resolvePath(variables, key);
    return resolved === undefined || resolved === '';
  });
}

/**
 * Escape a value so it won't be interpreted as a template variable if
 * re-processed. Useful when user-provided values might themselves contain `${...}`.
 *
 * @param value - The string to escape
 * @returns Escaped string
 */
export function escapeValue(value: string): string {
  return value.replace(/\$\{/g, '\\${');
}

/**
 * A convenience wrapper that applies the template and optionally pipes the
 * result through a markdown renderer (e.g. `marked`, `markdown-it`).
 *
 * @param template         - The markdown template string
 * @param variables        - Map of variable name → value
 * @param markdownRenderer - Optional renderer function
 * @param options          - Same options as applyTemplate
 * @returns Substituted markdown, or rendered HTML if a renderer is provided
 *
 * @example
 * // With marked:
 * import { marked } from 'marked';
 * renderTemplate("# Hello ${name}", { name: "Alex" }, marked);
 * // => "<h1>Hello Alex</h1>\n"
 *
 * @example
 * // Without renderer (returns plain substituted markdown):
 * renderTemplate("# Hello ${name}", { name: "Alex" });
 * // => "# Hello Alex"
 */
export function renderTemplate(
  template: string,
  variables: TemplateVariables,
  locale: string,
  markdownRenderer?: MarkdownRenderer,
  options: ApplyOptions = {}
): string {
  const substituted = applyTemplate(template, variables, locale, options);
  return markdownRenderer ? markdownRenderer(substituted) : substituted;
}
